/**
 * Preview routing helpers.
 *
 * The FE does NOT work out what stack a project is: the universal preview-
 * runner detects it from the project's real files and reports it (`stack` /
 * `stackLabel` on its status payload) — the FE only displays that. What is
 * left here is not stack detection:
 *   - `detectHelmChart`: Helm is an FE-only preview (no runner driver).
 *   - `detectServiceType`: frontend/backend classification from service
 *     metadata (tags / name / image) for listing and tab ordering.
 *   - `getContainerRoutingInfo`: constants for talking to the runner.
 */

import { devLog } from './devLog';

/** Port of the universal preview-runner (container-live/preview-runner). */
export const PREVIEW_RUNNER_PORT = 3025;
/** Pseudo-port that marks a Helm chart tab (rendered by HelmPreviewPanel, not the runner). */
export const HELM_PREVIEW_PORT = 3022;

/**
 * Detect if source code is a Helm chart
 * A Helm chart has Chart.yaml at root (with apiVersion: v2 or v1)
 * and optionally a templates/ directory.
 */
export const detectHelmChart = (sourceCode) => {
  const files = Object.keys(sourceCode || {});
  // Direct Chart.yaml at root
  if (sourceCode['Chart.yaml'] || sourceCode['Chart.yml']) {
    return true;
  }
  // Chart.yaml in any subdirectory (e.g. helm/Chart.yaml)
  if (files.some(f => f.endsWith('/Chart.yaml') || f.endsWith('/Chart.yml'))) {
    return true;
  }
  // Fallback: templates/ dir alongside a values.yaml (anywhere in the tree)
  const hasTemplates = files.some(f => f.startsWith('templates/') || f.includes('/templates/'));
  const hasValues = files.some(f => f === 'values.yaml' || f === 'values.yml' || f.endsWith('/values.yaml') || f.endsWith('/values.yml'));
  return hasTemplates && hasValues;
};

/**
 * Routing info for talking to the preview runner. Nothing here names a stack.
 * @param {Object} service - The service object from Mia Platform
 * @param {Object} [sourceCode] - Files, only used to spot a Helm chart
 * @param {Object} options - Optional context { activePreviewTab, previewSessionId }
 */
export const getContainerRoutingInfo = (service, sourceCode, options = {}) => {
  const { activePreviewTab, previewSessionId } = options;

  if (!service) {
    return null;
  }

  const isHelm = !!sourceCode && detectHelmChart(sourceCode);
  const serviceType = detectServiceType(service);
  const isBackend = isHelm || serviceType.isBackend;

  return {
    isBackend,
    isFrontend: !isBackend,
    isCrud: !isHelm && serviceType.isCrud,
    isHelm,
    containerPort: isHelm ? HELM_PREVIEW_PORT : PREVIEW_RUNNER_PORT,
    containerName: 'universal',
    sessionId: activePreviewTab?.sessionId || previewSessionId
  };
};

/**
 * Classify a service as frontend/backend from its metadata (tags, name, image).
 * @param {Object} service - The service object from Mia Platform
 * @returns {Object} - {isFrontend: boolean, isBackend: boolean, isCrud: boolean, confidence: string}
 */
export const detectServiceType = (service) => {
  if (!service) {
    return {isFrontend: false, isBackend: false, isCrud: false, confidence: 'none'};
  }
  
  // PRIORITY 1: Check explicit tags (high confidence)
  if (service.tags) {
    if (service.tags.includes('frontend') || service.tags.includes('fe')) {
      return {isFrontend: true, isBackend: false, isCrud: false, confidence: 'high'};
    }
    if (service.tags.includes('backend') || service.tags.includes('be')) {
      return {isFrontend: false, isBackend: true, isCrud: false, confidence: 'high'};
    }
    if (service.tags.includes('crud') || service.tags.includes('crud-service')) {
      return {isFrontend: false, isBackend: true, isCrud: true, confidence: 'high'};
    }
  }
  
  // Check if service has a repository URL (indicates source code is available)
  const hasSourceCode = !!(service.repoUrl || service.sshUrl || service.repositoryUrl);
  
  // PRIORITY 2: Check service name patterns (medium confidence)
  const name = (service.name || '').toLowerCase();
  const frontendPatterns = ['frontend', 'fe', 'react', 'vue', 'angular', 'svelte', 'next', 'web', 'ui', 'client', 'native', 'app', 'docusaurus', 'docs'];
  const backendPatterns = ['backend', 'be', 'api', 'server', 'service', 'node', 'go', 'python', 'java', 'dotnet', 'rust'];
  const crudPatterns = ['crud', 'crud-service', 'collection'];
  
  const hasFrontendName = frontendPatterns.some(pattern => name.includes(pattern));
  const hasBackendName = backendPatterns.some(pattern => name.includes(pattern));
  const hasCrudName = crudPatterns.some(pattern => name.includes(pattern));
  
  // Debug logging
  devLog(`🔍 detectServiceType from METADATA for "${service.name}":`, {
    hasFrontendName,
    hasBackendName,
    hasCrudName,
    hasSourceCode
  });
  
  // Check docker image patterns (medium confidence)
  const dockerImage = (service.dockerImage || '').toLowerCase();
  const hasFrontendImage = dockerImage.includes('nginx') || dockerImage.includes('react') || 
                          dockerImage.includes('vue') || dockerImage.includes('angular') ||
                          dockerImage.includes('frontend') || dockerImage.includes('vite');
  const hasBackendImage = dockerImage.includes('node') || dockerImage.includes('express') || 
                          dockerImage.includes('fastify') || dockerImage.includes('go') ||
                          dockerImage.includes('python') || dockerImage.includes('java') ||
                          dockerImage.includes('dotnet') || dockerImage.includes('rust');
  
  // Combine signals - prioritize BACKEND detection (backend patterns are more specific)
  if (hasCrudName) {
    return {isFrontend: false, isBackend: true, isCrud: true, confidence: 'medium'};
  }
  if (hasBackendName || hasBackendImage) {
    return {isFrontend: false, isBackend: true, isCrud: false, confidence: 'medium'};
  }
  if (hasFrontendName || hasFrontendImage) {
    return {isFrontend: true, isBackend: false, isCrud: false, confidence: 'medium'};
  }
  
  // Default: no clear signal
  return {isFrontend: false, isBackend: false, isCrud: false, confidence: 'none'};
};
